// Firebase service for handling mention notifications
import { 
  collection, 
  doc, 
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query, 
  where, 
  orderBy, 
  limit,
  startAfter,
  serverTimestamp,
  writeBatch
} from "firebase/firestore";
import { db } from "./firebase-config";
import { getUserNotificationPreferences, getUserProfile } from "./user-profile";

/**
 * Create mention notifications for tagged users
 * @param {Array} mentions - Array of mention objects
 * @param {Object} contentData - Content data (post/reply)
 * @returns {Promise<Object>} - Success status and created notifications
 */
export const createMentionNotifications = async (mentions, contentData) => {
  try {
    console.log('Creating mention notifications:', mentions.length);
    
    if (!mentions || mentions.length === 0) {
      return { success: true, data: [] };
    }
    
    // Filter out self-mentions and duplicates
    const uniqueMentions = mentions
      .filter(mention => mention.userId !== contentData.authorId)
      .filter((mention, index, arr) => 
        arr.findIndex(m => m.userId === mention.userId) === index
      );
    
    if (uniqueMentions.length === 0) {
      return { success: true, data: [] };
    }
    
    const batch = writeBatch(db);
    const notifications = [];
    
    // Batch check for duplicate notifications to reduce database calls
    const duplicateChecks = await Promise.all(
      uniqueMentions.map(mention => 
        checkDuplicateNotification(mention.userId, contentData.id, contentData.authorId)
      )
    );
    
    for (let i = 0; i < uniqueMentions.length; i++) {
      const mention = uniqueMentions[i];
      const isDuplicate = duplicateChecks[i];
      
      if (isDuplicate) {
        console.log(`Duplicate notification detected for user ${mention.userId}, content ${contentData.id}`);
        continue;
      }
      
      const notificationData = {
        mentionedUserId: mention.userId,
        mentionerUserId: contentData.authorId,
        mentionerName: contentData.authorName,
        mentionerPhotoURL: contentData.authorPhotoURL || '',
        contentType: contentData.type, // 'question' or 'reply'
        contentId: contentData.id,
        questionId: contentData.questionId || contentData.id,
        questionTitle: contentData.title || contentData.questionTitle || 'Forum Discussion',
        contentPreview: generateContentPreview(contentData.content),
        mentionText: mention.displayName,
        isRead: false,
        createdAt: serverTimestamp(),
        readAt: null
      };
      
      // Create notification document
      const notificationRef = doc(collection(db, 'forum_mention_notifications'));
      batch.set(notificationRef, notificationData);
      
      notifications.push({
        id: notificationRef.id,
        ...notificationData
      });
    }
    
    // Commit all notifications in batch
    await batch.commit();
    
    console.log(`Created ${notifications.length} mention notifications`);
    
    // Trigger email notifications in background (non-blocking)
    triggerMentionEmailNotifications(notifications, contentData)
      .catch(error => {
        console.error('Background email notification processing failed:', error);
        // Don't fail the main operation
      });
    
    return {
      success: true,
      data: notifications
    };
    
  } catch (error) {
    console.error('Error creating mention notifications:', error);
    return {
      success: false,
      error: error.message || 'Failed to create mention notifications'
    };
  }
};

/**
 * Get mention notifications for a user
 * @param {string} userId - User ID to get notifications for
 * @param {Object} options - Query options
 * @returns {Promise<Object>} - Notifications data
 */
export const getMentionNotifications = async (userId, options = {}) => {
  try {
    const {
      limitCount = 20,
      unreadOnly = false,
      lastVisible = null
    } = options;
    
    console.log(`Fetching mention notifications for user: ${userId}`);
    
    let notificationsQuery = collection(db, 'forum_mention_notifications');
    const queryConstraints = [
      where('mentionedUserId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    ];
    
    if (unreadOnly) {
      queryConstraints.splice(1, 0, where('isRead', '==', false));
    }
    
    if (lastVisible) {
      queryConstraints.push(startAfter(lastVisible));
    }
    
    notificationsQuery = query(notificationsQuery, ...queryConstraints);
    
    const snapshot = await getDocs(notificationsQuery);
    
    const notifications = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return {
      success: true,
      data: {
        notifications,
        lastVisible: snapshot.docs[snapshot.docs.length - 1] || null,
        hasMore: snapshot.docs.length === limitCount
      }
    };
    
  } catch (error) {
    console.error('Error fetching mention notifications:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch mention notifications',
      data: { notifications: [], lastVisible: null, hasMore: false }
    };
  }
};

/**
 * Mark mention notification as read
 * @param {string} notificationId - Notification ID to mark as read
 * @param {string} userId - User ID (for security)
 * @returns {Promise<Object>} - Success status
 */
export const markMentionNotificationAsRead = async (notificationId, userId) => {
  try {
    const notificationRef = doc(db, 'forum_mention_notifications', notificationId);
    
    await updateDoc(notificationRef, {
      isRead: true,
      readAt: serverTimestamp()
    });
    
    return { success: true };
    
  } catch (error) {
    console.error('Error marking mention notification as read:', error);
    return {
      success: false,
      error: error.message || 'Failed to mark notification as read'
    };
  }
};

/**
 * Mark all mention notifications as read for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Success status
 */
export const markAllMentionNotificationsAsRead = async (userId) => {
  try {
    const notificationsQuery = query(
      collection(db, 'forum_mention_notifications'),
      where('mentionedUserId', '==', userId),
      where('isRead', '==', false)
    );
    
    const snapshot = await getDocs(notificationsQuery);
    
    if (snapshot.empty) {
      return { success: true };
    }
    
    const batch = writeBatch(db);
    
    snapshot.docs.forEach(doc => {
      batch.update(doc.ref, {
        isRead: true,
        readAt: serverTimestamp()
      });
    });
    
    await batch.commit();
    
    console.log(`Marked ${snapshot.docs.length} notifications as read for user: ${userId}`);
    
    return { success: true };
    
  } catch (error) {
    console.error('Error marking all mention notifications as read:', error);
    return {
      success: false,
      error: error.message || 'Failed to mark all notifications as read'
    };
  }
};

/**
 * Delete mention notification
 * @param {string} notificationId - Notification ID to delete
 * @param {string} userId - User ID (for security)
 * @returns {Promise<Object>} - Success status
 */
export const deleteMentionNotification = async (notificationId, userId) => {
  try {
    const notificationRef = doc(db, 'forum_mention_notifications', notificationId);
    await deleteDoc(notificationRef);
    
    return { success: true };
    
  } catch (error) {
    console.error('Error deleting mention notification:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete notification'
    };
  }
};

/**
 * Get unread mention count for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Unread count
 */
export const getUnreadMentionCount = async (userId) => {
  try {
    const notificationsQuery = query(
      collection(db, 'forum_mention_notifications'),
      where('mentionedUserId', '==', userId),
      where('isRead', '==', false)
    );
    
    const snapshot = await getDocs(notificationsQuery);
    
    return {
      success: true,
      data: { count: snapshot.docs.length }
    };
    
  } catch (error) {
    console.error('Error getting unread mention count:', error);
    return {
      success: false,
      error: error.message || 'Failed to get unread count',
      data: { count: 0 }
    };
  }
};

/**
 * Clean up old mention notifications
 * @param {number} daysOld - Delete notifications older than this many days
 * @returns {Promise<Object>} - Cleanup result
 */
export const cleanupOldMentionNotifications = async (daysOld = 90) => {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);
    
    const oldNotificationsQuery = query(
      collection(db, 'forum_mention_notifications'),
      where('createdAt', '<', cutoffDate),
      limit(500) // Process in batches
    );
    
    const snapshot = await getDocs(oldNotificationsQuery);
    
    if (snapshot.empty) {
      return { success: true, deletedCount: 0 };
    }
    
    const batch = writeBatch(db);
    
    snapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    
    await batch.commit();
    
    console.log(`Deleted ${snapshot.docs.length} old mention notifications`);
    
    return {
      success: true,
      deletedCount: snapshot.docs.length
    };
    
  } catch (error) {
    console.error('Error cleaning up old mention notifications:', error);
    return {
      success: false,
      error: error.message || 'Failed to cleanup old notifications',
      deletedCount: 0
    };
  }
};

/**
 * Trigger email notifications for mentions
 * @param {Array} notifications - Created mention notifications
 * @param {Object} contentData - Content context
 * @returns {Promise<Object>} - Email trigger results
 */
export const triggerMentionEmailNotifications = async (notifications, contentData) => {
  try {
    console.log(`Triggering email notifications for ${notifications.length} mentions`);
    
    if (!notifications || notifications.length === 0) {
      console.log('No notifications to process for email triggers');
      return { success: true, emailsSent: 0 };
    }
    
    let emailsSent = 0;
    const errors = [];
    const rateLimitedUsers = [];
    
    for (const notification of notifications) {
      try {
        // Get user preferences to check if tag notifications are enabled
        const userPrefsResult = await getUserNotificationPreferences(notification.mentionedUserId);
        
        if (!userPrefsResult.success) {
          console.warn(`Failed to get preferences for user ${notification.mentionedUserId}`);
          continue;
        }
        
        const preferences = userPrefsResult.data.preferences;
        
        // Check if email notifications are enabled
        if (!preferences.email?.enabled) {
          console.log(`Email notifications disabled for user ${notification.mentionedUserId}`, {
            userId: notification.mentionedUserId,
            reason: 'email_disabled'
          });
          continue;
        }
        
        // Check if forum notifications are enabled
        if (!preferences.forum?.enabled) {
          console.log(`Forum notifications disabled for user ${notification.mentionedUserId}`, {
            userId: notification.mentionedUserId,
            reason: 'forum_disabled'
          });
          continue;
        }
        
        // Check if tag notifications are enabled
        if (!preferences.forum?.tagNotifications) {
          console.log(`Tag notifications disabled for user ${notification.mentionedUserId}`, {
            userId: notification.mentionedUserId,
            reason: 'tag_notifications_disabled'
          });
          continue;
        }
        
        // Check rate limiting for this user
        const rateLimitResult = await checkTagEmailRateLimit(notification.mentionedUserId);
        if (!rateLimitResult.allowed) {
          console.log(`Rate limit exceeded for user ${notification.mentionedUserId}`, {
            userId: notification.mentionedUserId,
            reason: 'rate_limit_exceeded',
            count: rateLimitResult.count,
            windowStart: rateLimitResult.windowStart
          });
          rateLimitedUsers.push(notification.mentionedUserId);
          continue;
        }
        
        // Get user data for email
        const userResult = await getUserProfile(notification.mentionedUserId);
        if (!userResult.success || !userResult.data.email) {
          console.warn(`Failed to get user data for ${notification.mentionedUserId}`);
          continue;
        }
        
        const userData = userResult.data;
        
        // Prepare tag notification data
        const tagData = {
          taggerName: notification.mentionerName,
          postTitle: notification.questionTitle,
          postUrl: generatePostUrl(notification.questionId, notification.contentId, notification.contentType),
          isReply: notification.contentType === 'reply'
        };
        
        // Queue email notification (this would typically be done via Cloud Functions)
        // For now, we'll create a notification queue entry
        await queueTagEmailNotification(userData, tagData, preferences);
        
        // Update rate limit counter
        await updateTagEmailRateLimit(notification.mentionedUserId);
        
        emailsSent++;
        
      } catch (error) {
        console.error(`Error processing email notification for user ${notification.mentionedUserId}:`, error);
        errors.push({
          userId: notification.mentionedUserId,
          error: error.message
        });
      }
    }
    
    console.log(`Successfully queued ${emailsSent} tag email notifications`, {
      totalNotifications: notifications.length,
      emailsSent,
      errorCount: errors.length,
      rateLimitedCount: rateLimitedUsers.length,
      successRate: notifications.length > 0 ? (emailsSent / notifications.length * 100).toFixed(2) + '%' : '0%'
    });
    
    return {
      success: true,
      emailsSent,
      rateLimitedUsers: rateLimitedUsers.length > 0 ? rateLimitedUsers : undefined,
      errors: errors.length > 0 ? errors : undefined
    };
    
  } catch (error) {
    console.error('Error triggering mention email notifications:', error);
    return {
      success: false,
      error: error.message || 'Failed to trigger email notifications',
      emailsSent: 0
    };
  }
};

/**
 * Queue tag email notification for processing
 * @param {Object} userData - User data
 * @param {Object} tagData - Tag notification data
 * @param {Object} preferences - User preferences
 * @returns {Promise<Object>} - Queue result
 */
const queueTagEmailNotification = async (userData, tagData, preferences) => {
  try {
    // Create email notification queue entry
    const queueData = {
      type: 'tag_notification',
      recipientId: userData.userId,
      recipientEmail: userData.email,
      recipientName: userData.name || userData.firstName || 'there',
      tagData,
      preferences,
      createdAt: serverTimestamp(),
      processed: false,
      attempts: 0
    };
    
    // Add to email queue collection
    await addDoc(collection(db, 'email_notification_queue'), queueData);
    
    console.log(`Queued tag email notification for user ${userData.userId}`);
    
    return { success: true };
    
  } catch (error) {
    console.error('Error queuing tag email notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Generate post URL based on content type
 * @param {string} questionId - Question ID
 * @param {string} contentId - Content ID
 * @param {string} contentType - Content type ('question' or 'reply')
 * @returns {string} - Post URL
 */
const generatePostUrl = (questionId, contentId, contentType) => {
  const baseUrl = process.env.REACT_APP_BASE_URL || 'https://justburnt.com';
  
  if (contentType === 'question') {
    return `${baseUrl}/forum/${questionId}`;
  } else {
    // For replies, link to the question with reply anchor
    return `${baseUrl}/forum/${questionId}#reply-${contentId}`;
  }
};

/**
 * Check rate limit for tag email notifications
 * @param {string} userId - User ID to check
 * @returns {Promise<{allowed: boolean, count: number, windowStart: Date}>}
 */
const checkTagEmailRateLimit = async (userId) => {
  try {
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    
    // Query for tag email notifications sent in the last hour
    const rateLimitQuery = query(
      collection(db, 'email_notification_queue'),
      where('recipientId', '==', userId),
      where('type', '==', 'tag_notification'),
      where('createdAt', '>=', oneHourAgo)
    );
    
    const snapshot = await getDocs(rateLimitQuery);
    const count = snapshot.docs.length;
    
    // Allow maximum 10 tag emails per hour per user
    const maxEmailsPerHour = 10;
    const allowed = count < maxEmailsPerHour;
    
    return {
      allowed,
      count,
      windowStart: oneHourAgo,
      limit: maxEmailsPerHour
    };
    
  } catch (error) {
    console.error('Error checking rate limit:', error);
    // On error, allow the email to prevent blocking legitimate notifications
    return { allowed: true, count: 0, windowStart: new Date() };
  }
};

/**
 * Update rate limit counter (this is handled by the queue entry creation)
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
const updateTagEmailRateLimit = async (userId) => {
  // Rate limiting is tracked by the email_notification_queue entries
  // No additional action needed as the queue entry creation handles this
  console.log(`Rate limit updated for user ${userId}`);
};

/**
 * Check for duplicate notifications to prevent spam
 * @param {string} userId - User ID
 * @param {string} contentId - Content ID
 * @param {string} taggerId - Tagger user ID
 * @returns {Promise<boolean>} - True if duplicate found
 */
const checkDuplicateNotification = async (userId, contentId, taggerId) => {
  try {
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    
    // Check for recent identical notifications
    const duplicateQuery = query(
      collection(db, 'forum_mention_notifications'),
      where('mentionedUserId', '==', userId),
      where('contentId', '==', contentId),
      where('mentionerUserId', '==', taggerId),
      where('createdAt', '>=', fiveMinutesAgo)
    );
    
    const snapshot = await getDocs(duplicateQuery);
    return !snapshot.empty;
    
  } catch (error) {
    console.error('Error checking duplicate notification:', error);
    return false; // On error, allow the notification
  }
};

/**
 * Generate content preview for notifications
 * @param {string} content - Full content
 * @returns {string} - Preview text
 */
const generateContentPreview = (content) => {
  if (!content) return '';
  
  // Remove HTML tags and get plain text
  const plainText = content.replace(/<[^>]*>/g, '').trim();
  
  // Limit to 100 characters
  if (plainText.length <= 100) {
    return plainText;
  }
  
  return plainText.substring(0, 97) + '...';
};